import React, { useState, useEffect } from 'react';
import { Button, Input, Select } from '@/components/ui';
import type { PlanningPeriod, PlanningPeriodStatus } from '@/types';

interface PlanningPeriodFormProps {
  initialData?: PlanningPeriod | null;
  onSubmit: (data: Partial<PlanningPeriod>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const PlanningPeriodForm: React.FC<PlanningPeriodFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    status: 'DRAFT' as PlanningPeriodStatus
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        startDate: initialData.startDate.split('T')[0], // Convert to YYYY-MM-DD format
        endDate: initialData.endDate.split('T')[0],
        status: initialData.status
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date';
      }

      // Check if it's a reasonable planning period (at least 6 months, max 2 years)
      const diffMonths = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
      if (diffMonths < 6) {
        newErrors.endDate = 'Planning period should be at least 6 months';
      }
      if (diffMonths > 24) {
        newErrors.endDate = 'Planning period should not exceed 2 years';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Convert dates to ISO format
    const submitData = {
      ...formData,
      startDate: new Date(formData.startDate).toISOString(),
      endDate: new Date(formData.endDate).toISOString()
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const statusOptions = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'CLOSED', label: 'Closed' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Input
        label="Planning Period Name"
        value={formData.name}
        onChange={(e) => handleInputChange('name', e.target.value)}
        error={errors.name}
        placeholder="e.g., FY 2024 Planning"
        required
        fullWidth
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="Start Date"
          type="date"
          value={formData.startDate}
          onChange={(e) => handleInputChange('startDate', e.target.value)}
          error={errors.startDate}
          required
          fullWidth
        />

        <Input
          label="End Date"
          type="date"
          value={formData.endDate}
          onChange={(e) => handleInputChange('endDate', e.target.value)}
          error={errors.endDate}
          required
          fullWidth
        />
      </div>

      <Select
        label="Status"
        value={formData.status}
        onChange={(e) => handleInputChange('status', e.target.value)}
        options={statusOptions}
        fullWidth
        helperText={
          formData.status === 'ACTIVE'
            ? 'Only one planning period can be active at a time'
            : formData.status === 'CLOSED'
            ? 'Closed periods cannot be modified'
            : 'Draft periods can be edited and activated later'
        }
      />

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {initialData ? 'Update' : 'Create'} Planning Period
        </Button>
      </div>
    </form>
  );
};

export default PlanningPeriodForm;